import { Link, useParams } from '@tanstack/react-router';
import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui.tsx';

/**
 * Modo "somente consulta" das telas de cadastros corporativos (Dados Mestres). As telas de Sistema
 * continuam como sempre (o padrão é sem este modo). Quando outro departamento abre a mesma tela
 * (FP&A › Configurações), ela só consulta: somem o Novo, o Editar e o Inativar, e aparece o atalho
 * "Gerenciar em Sistema" para quem já tem a permissão de manter o cadastro.
 */
const ReadOnlyContext = createContext<{ manageTo: string } | null>(null);

export function MasterDataReadOnly({
  manageTo,
  children,
}: {
  /** A tela oficial do cadastro em Sistema. */
  manageTo: string;
  children: ReactNode;
}) {
  return <ReadOnlyContext.Provider value={{ manageTo }}>{children}</ReadOnlyContext.Provider>;
}

/** Verdadeiro quando a tela foi aberta em modo somente consulta. */
export function useMasterDataReadOnly(): boolean {
  return useContext(ReadOnlyContext) !== null;
}

/**
 * Aviso de somente consulta e o atalho para Sistema. O atalho só aparece para quem já pode manter o
 * cadastro (`allowed`); ele não dá permissão nenhuma: a tela de Sistema e a API conferem de novo.
 */
export function MasterDataNote({ allowed }: { allowed: boolean }) {
  const { t } = useTranslation();
  const ctx = useContext(ReadOnlyContext);
  if (!ctx) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-muted">{t('masterData.readOnly')}</p>
      {allowed && (
        <Link to={ctx.manageTo}>
          <Button variant="secondary">{t('masterData.manageInSystem')}</Button>
        </Link>
      )}
    </div>
  );
}

/** As tabelas de classificação: o atalho leva à mesma tabela em Sistema › Estrutura Funcional. */
export function ClassificationsReadOnly({ children }: { children: ReactNode }) {
  const { category } = useParams({ strict: false }) as { category: string };
  return (
    <MasterDataReadOnly manageTo={`/structure/classifications/${category}`}>
      {children}
    </MasterDataReadOnly>
  );
}
